import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Card } from '@/components/Card';
import { useApi } from '@/hooks/useApi';
import { post, patch, del } from '@/lib/api';

interface PTOPolicy {
  id: string;
  name: string;
  policy_type: 'accrual' | 'flat_allocation' | 'unlimited';
  accrual_rate?: number;
  accrual_frequency?: string;
  accrual_cap?: number;
  annual_allocation?: number;
  allocation_unit?: string;
  max_carryover?: number;
  waiting_period_days: number;
  approval_required: boolean;
  active: boolean;
  assigned_to: 'all' | 'specific';
  employee_count: number;
}

interface PTOPolicyForm {
  name: string;
  policy_type: 'accrual' | 'flat_allocation' | 'unlimited';
  accrual_rate: number;
  accrual_frequency: 'per_pay_period' | 'monthly' | 'annually' | 'front_loaded';
  accrual_cap: number;
  annual_allocation: number;
  allocation_unit: 'hours' | 'days';
  max_carryover: number;
  waiting_period_days: number;
  approval_required: boolean;
  active: boolean;
  assigned_to: 'all' | 'specific';
  assigned_department_ids: string[];
  assigned_role_ids: string[];
}

const EMPTY_FORM: PTOPolicyForm = {
  name: '',
  policy_type: 'accrual',
  accrual_rate: 4,
  accrual_frequency: 'monthly',
  accrual_cap: 0,
  annual_allocation: 80,
  allocation_unit: 'hours',
  max_carryover: 40,
  waiting_period_days: 0,
  approval_required: false,
  active: true,
  assigned_to: 'all',
  assigned_department_ids: [],
  assigned_role_ids: [],
};

export function PTOPoliciesAdminPage() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PTOPolicyForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const { data, refetch } = useApi<{ policies: PTOPolicy[] }>('admin/pto-policies');

  const policies = data?.policies ?? [];

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  }

  function openEdit(p: PTOPolicy) {
    setForm({
      name: p.name,
      policy_type: p.policy_type as PTOPolicyForm['policy_type'],
      accrual_rate: p.accrual_rate ?? 4,
      accrual_frequency: (p.accrual_frequency ?? 'monthly') as PTOPolicyForm['accrual_frequency'],
      accrual_cap: p.accrual_cap ?? 0,
      annual_allocation: p.annual_allocation ?? 80,
      allocation_unit: (p.allocation_unit ?? 'hours') as PTOPolicyForm['allocation_unit'],
      max_carryover: p.max_carryover ?? 40,
      waiting_period_days: p.waiting_period_days,
      approval_required: p.approval_required,
      active: p.active,
      assigned_to: p.assigned_to as PTOPolicyForm['assigned_to'],
      assigned_department_ids: [],
      assigned_role_ids: [],
    });
    setEditingId(p.id);
    setShowForm(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (editingId) {
        await patch(`admin/pto-policies/${editingId}`, form);
      } else {
        await post('admin/pto-policies', form);
      }
      setShowForm(false);
      refetch();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete policy "${name}"?`)) return;
    await del(`admin/pto-policies/${id}`);
    refetch();
  }

  async function toggleActive(p: PTOPolicy) {
    await patch(`admin/pto-policies/${p.id}`, { active: !p.active });
    refetch();
  }

  const policyTypeLabel: Record<string, string> = {
    accrual: 'Accrual',
    flat_allocation: 'Flat Allocation',
    unlimited: 'Unlimited',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin')} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold text-foreground">PTO Policies</h2>
        <button onClick={openCreate} className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-black text-sm font-medium rounded-lg transition-colors">
          <Plus className="w-4 h-4" /> New Policy
        </button>
      </div>

      {showForm && (
        <Card className="border border-amber-500/30">
          <h3 className="text-sm font-semibold text-foreground mb-4">{editingId ? 'Edit Policy' : 'New PTO Policy'}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Policy Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" placeholder="e.g. Vacation" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Policy Type *</label>
              <select value={form.policy_type} onChange={e => setForm(f => ({ ...f, policy_type: e.target.value as typeof form.policy_type }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                <option value="accrual">Accrual-based</option>
                <option value="flat_allocation">Flat Allocation</option>
                <option value="unlimited">Unlimited</option>
              </select>
            </div>

            {form.policy_type === 'accrual' && (
              <>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Accrual Rate (hours)</label>
                  <input type="number" value={form.accrual_rate} onChange={e => setForm(f => ({ ...f, accrual_rate: parseFloat(e.target.value) }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Accrual Frequency</label>
                  <select value={form.accrual_frequency} onChange={e => setForm(f => ({ ...f, accrual_frequency: e.target.value as typeof form.accrual_frequency }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                    <option value="per_pay_period">Per Pay Period</option>
                    <option value="monthly">Monthly</option>
                    <option value="annually">Annually</option>
                    <option value="front_loaded">Front Loaded</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Accrual Cap (hours, 0 = no cap)</label>
                  <input type="number" value={form.accrual_cap} onChange={e => setForm(f => ({ ...f, accrual_cap: parseFloat(e.target.value) }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
                </div>
              </>
            )}

            {form.policy_type === 'flat_allocation' && (
              <>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Annual Allocation</label>
                  <input type="number" value={form.annual_allocation} onChange={e => setForm(f => ({ ...f, annual_allocation: parseFloat(e.target.value) }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Unit</label>
                  <select value={form.allocation_unit} onChange={e => setForm(f => ({ ...f, allocation_unit: e.target.value as typeof form.allocation_unit }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </select>
                </div>
              </>
            )}

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Max Carryover (hours, 0 = no carryover)</label>
              <input type="number" value={form.max_carryover} onChange={e => setForm(f => ({ ...f, max_carryover: parseFloat(e.target.value) }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Waiting Period (days before use)</label>
              <input type="number" value={form.waiting_period_days} onChange={e => setForm(f => ({ ...f, waiting_period_days: parseInt(e.target.value) }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input type="checkbox" checked={form.approval_required} onChange={e => setForm(f => ({ ...f, approval_required: e.target.checked }))} className="rounded" />
                Approval Required
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded" />
                Active
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} disabled={saving || !form.name} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black text-sm font-medium rounded-lg transition-colors">
              {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-surface-2 hover:bg-surface-3 text-foreground text-sm rounded-lg transition-colors">Cancel</button>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {policies.length === 0 && !showForm && (
          <div className="text-center py-12 text-muted-foreground text-sm">No PTO policies yet. Create one to get started.</div>
        )}
        {policies.map(p => (
          <Card key={p.id}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{p.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">{policyTypeLabel[p.policy_type]}</span>
                  {!p.active && <span className="text-xs px-2 py-0.5 rounded-full bg-surface-3 text-muted-foreground">Inactive</span>}
                </div>
                <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                  {p.policy_type === 'accrual' && <span>{p.accrual_rate}h per {p.accrual_frequency?.replace('_', ' ')}</span>}
                  {p.policy_type === 'flat_allocation' && <span>{p.annual_allocation} {p.allocation_unit}/year</span>}
                  {p.policy_type === 'unlimited' && <span>Unlimited time off</span>}
                  {(p.max_carryover ?? 0) > 0 && <span>Carryover: {p.max_carryover}h max</span>}
                  {p.waiting_period_days > 0 && <span>{p.waiting_period_days}d waiting period</span>}
                  <span>Assigned to: {p.assigned_to === 'all' ? 'All employees' : 'Specific groups'}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleActive(p)} className="text-muted-foreground hover:text-amber-400 transition-colors" title={p.active ? 'Deactivate' : 'Activate'}>
                  {p.active ? <ToggleRight className="w-5 h-5 text-amber-400" /> : <ToggleLeft className="w-5 h-5" />}
                </button>
                <button onClick={() => openEdit(p)} className="text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(p.id, p.name)} className="text-muted-foreground hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
